import React, { useState, useEffect } from 'react';
import { LuLock, LuX, LuDelete } from 'react-icons/lu';
import {
  useSendPhonicsMessageToTeacherMutation,
  PhonicsMessageType,
} from '@/api/types';
import { getPasscodeFromUuid } from '@/utils/uuid';
import { useData } from '@/context/Data';
import { Parrot } from '@/components';

interface LockScreenButtonProps {
  value:
    | 'del'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7'
    | '8'
    | '9'
    | '0'
    | null;
  onClick?: () => void;
}

export const LockScreenButton = ({ value, onClick }: LockScreenButtonProps) => {
  if (value === null) {
    return <div>&nbsp;</div>;
  }

  return (
    <button
      className="flex items-center justify-center bg-white text-pt-ds-ink rounded-full w-20 h-20 text-4xl shadow-[0_8px_0_rgba(0,0,0,0.15)] transition-transform hover:-translate-y-0.5 active:translate-y-1 focus:bg-[var(--c-success)] focus:text-white"
      onClick={onClick}
    >
      {value === 'del' ? <LuDelete /> : value}
    </button>
  );
};
interface LockScreenProps {
  onUnlock: () => void;
  onClose: () => void;
}

export const LockScreen = ({ onUnlock, onClose }: LockScreenProps) => {
  const { currentClass } = useData();

  const [inputCode, setInputCode] = useState('');

  const [unlockCode, setUnlockCode] = useState<string | null>(null);

  const [sendPhonicsMessageToTeacher, {}] =
    useSendPhonicsMessageToTeacherMutation();

  useEffect(() => {
    if (!unlockCode) {
      return;
    }

    if (inputCode.length > unlockCode.length) {
      setInputCode('');
    }

    if (inputCode === unlockCode) {
      onUnlock();
    }
  }, [inputCode, onUnlock, unlockCode]);

  useEffect(() => {
    if (currentClass?.value) {
      sendPhonicsMessageToTeacher({
        variables: {
          classId: currentClass.value,
          messageType: PhonicsMessageType.LOCK_SCREEN,
        },
      });

      setUnlockCode(getPasscodeFromUuid(currentClass.value));
    } else {
      console.error('No class selected');
    }
  }, [currentClass, sendPhonicsMessageToTeacher]);

  return (
    <div className="fixed top-0 left-0 flex gap-6 z-50 w-screen h-screen bg-pt-coral-red justify-center items-center">
      <div className="flex w-[800px] h-[600px]">
        <div className="flex-1 flex justify-center items-center">
          <Parrot
            type="speaking"
            className="w-3/4"
            content={
              <p>
                <strong>Woops!</strong>
                <br />
                Don&apos;t worry, your teacher is on the way to help!
              </p>
            }
          />
        </div>

        <div className="flex-1">
          <div className="flex flex-col gap-6 w-64 mx-auto mt-12">
            <LuLock className="w-12 h-12 mx-auto text-white" />

            <div className="h-14 px-6 py-3 text-2xl tracking-[6px] text-center bg-white rounded-2xl border border-pt-ds-line text-pt-ds-ink shadow-[0_12px_32px_rgba(0,0,0,0.18)]">
              {inputCode}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <LockScreenButton
                key={'1'}
                value={'1'}
                onClick={() => setInputCode((prev) => prev + '1')}
              />

              <LockScreenButton
                key={'2'}
                value={'2'}
                onClick={() => setInputCode((prev) => prev + '2')}
              />

              <LockScreenButton
                key={'3'}
                value={'3'}
                onClick={() => setInputCode((prev) => prev + '3')}
              />

              <LockScreenButton
                key={'4'}
                value={'4'}
                onClick={() => setInputCode((prev) => prev + '4')}
              />

              <LockScreenButton
                key={'5'}
                value={'5'}
                onClick={() => setInputCode((prev) => prev + '5')}
              />

              <LockScreenButton
                key={'6'}
                value={'6'}
                onClick={() => setInputCode((prev) => prev + '6')}
              />

              <LockScreenButton
                key={'7'}
                value={'7'}
                onClick={() => setInputCode((prev) => prev + '7')}
              />

              <LockScreenButton
                key={'8'}
                value={'8'}
                onClick={() => setInputCode((prev) => prev + '8')}
              />

              <LockScreenButton
                key={'9'}
                value={'9'}
                onClick={() => setInputCode((prev) => prev + '9')}
              />

              <LockScreenButton key={'blank'} value={null} />

              <LockScreenButton
                key={'0'}
                value={'0'}
                onClick={() => setInputCode((prev) => prev + '0')}
              />

              <LockScreenButton
                key={'del'}
                value={'del'}
                onClick={() => setInputCode((prev) => prev.slice(0, -1))}
              />
            </div>
          </div>
        </div>
      </div>

      <button className="absolute top-6 right-6" onClick={onClose}>
        <LuX className="w-12 h-12 text-white hover:opacity-70" />
      </button>
    </div>
  );
};
